import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { mapAdminApiError } from "../lib/errors";

export type Column<T> = {
  /** Display label for the column header. */
  header: string;
  /** Optional css width (e.g. "12%"). */
  width?: string;
  /** Renders a single cell from the row. */
  cell: (row: T) => React.ReactNode;
  /** When true, the cell wraps and grows; otherwise it stays truncated. */
  wrap?: boolean;
  /** Optional className applied to the <td> element. */
  className?: string;
  /**
   * Hide this column on viewports narrower than the given Tailwind
   * breakpoint. Use to suppress non-essential signal (stripe badge,
   * plan tier, etc.) on phones without losing the row.
   *   * "sm" hides on <640px
   *   * "md" hides on <768px
   *   * "lg" hides on <1024px
   *   * "xl" hides on <1280px
   * The first column (usually name/email) should always stay visible
   * — omit the prop there.
   */
  hideBelow?: "sm" | "md" | "lg" | "xl";
};

function hideClass(bp: Column<unknown>["hideBelow"]): string {
  switch (bp) {
    case "sm":
      return "hidden sm:table-cell";
    case "md":
      return "hidden md:table-cell";
    case "lg":
      return "hidden lg:table-cell";
    case "xl":
      return "hidden xl:table-cell";
    default:
      return "";
  }
}

/** One page fetched by `fetchPage`, plus the cursor that was used to fetch it. */
type LoadedPage<T> = { items: T[]; nextCursor: string | null; cursorUsed?: string };

interface AdminTableProps<T> {
  /**
   * Fetches one page. `cursor` is omitted for the first page; `limit` is
   * always sent. Implementations wire this to `api.request("GET /admin/…")`.
   */
  fetchPage: (opts: {
    q: string;
    cursor?: string;
    limit: number;
  }) => Promise<{ items: T[]; next_cursor: string | null; meta?: Record<string, unknown> }>;
  /** Column definitions, in render order. */
  columns: Column<T>[];
  /** Stable row key extractor. */
  rowKey: (row: T) => string;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Empty-state title/desc when there are no rows at all. */
  emptyTitle?: string;
  emptyDescription?: string;
  /** Optional right-side filters above the table. */
  filters?: React.ReactNode;
  /** Page size sent as `limit`. Defaults to 20 to match the backend default. */
  pageSize?: number;
  /** Bump this to force a refetch from the first page (e.g. after a row mutation from the page). */
  refreshKey?: number;
}

/**
 * Generic admin list view. Owns search debounce + fetch lifecycle so each
 * admin page is just a column config + a typed `fetchPage`.
 *
 * Pagination is keyset/opaque-cursor, not offset: the API no longer accepts
 * an `offset` query param for these lists. "Anterior"/"Próxima" therefore
 * walk a client-side cache of already-fetched pages (built forward as the
 * user pages through, cached so going back never re-fetches) rather than
 * jumping to an arbitrary offset.
 */
export function AdminTable<T>({
  fetchPage,
  columns,
  rowKey,
  searchPlaceholder = "Buscar",
  emptyTitle = "Nada por aqui ainda",
  emptyDescription,
  filters,
  pageSize = 20,
  refreshKey = 0,
}: AdminTableProps<T>) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [pages, setPages] = useState<LoadedPage<T>[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // First page: refetched whenever the search, or refreshKey, changes.
  useEffect(() => {
    let alive = true;
    setPages([]);
    setPageIndex(0);
    setTotal(null);
    setError(null);
    setLoading(true);
    fetchPage({ q: debouncedQuery, limit: pageSize })
      .then((res) => {
        if (!alive) return;
        setPages([{ items: res.items, nextCursor: res.next_cursor }]);
        setTotal(typeof res.meta?.total === "number" ? res.meta.total : null);
      })
      .catch((err) => {
        if (!alive) return;
        setError(mapAdminApiError(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // fetchPage is expected to be referentially stable per page (defined
    // inline at the call site); debouncedQuery/pageSize/refreshKey are the
    // real dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, pageSize, refreshKey]);

  async function goNext() {
    const current = pages[pageIndex];
    if (!current) return;
    if (pageIndex + 1 < pages.length) {
      setPageIndex(pageIndex + 1);
      return;
    }
    if (!current.nextCursor) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPage({ q: debouncedQuery, cursor: current.nextCursor, limit: pageSize });
      setPages((prev) => [...prev, { items: res.items, nextCursor: res.next_cursor, cursorUsed: current.nextCursor! }]);
      if (typeof res.meta?.total === "number") setTotal(res.meta.total);
      setPageIndex((p) => p + 1);
    } catch (err) {
      setError(mapAdminApiError(err));
    } finally {
      setLoading(false);
    }
  }

  function goPrev() {
    setPageIndex((p) => Math.max(0, p - 1));
  }

  const items = pages[pageIndex]?.items ?? null;
  const hasNext = Boolean(pages[pageIndex]?.nextCursor) || pageIndex + 1 < pages.length;
  const hasPrev = pageIndex > 0;
  const totalPages = total !== null ? Math.max(1, Math.ceil(total / pageSize)) : null;

  if (error) {
    return (
      <div className="rounded-3xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-tertiary"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-full border border-border bg-surface py-2.5 pl-9 pr-4 text-sm text-text outline-none placeholder:text-text-tertiary focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>
        {filters}
        <p className="ml-auto hidden text-xs text-text-tertiary sm:block">
          {items === null
            ? "carregando…"
            : total !== null
              ? `${total} ${total === 1 ? "registro" : "registros"}`
              : `${items.length}${hasNext ? "+" : ""} ${items.length === 1 && !hasNext ? "registro" : "registros"}`}
        </p>
      </div>

      {items === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-16">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface p-12 text-center">
          <h2 className="text-base font-semibold tracking-tight text-text">
            {debouncedQuery ? `Nenhum resultado para "${debouncedQuery}".` : emptyTitle}
          </h2>
          {emptyDescription && !debouncedQuery && (
            <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
              {emptyDescription}
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-border bg-surface">
          {/* `table-auto` lets the remaining visible columns share width
              when others collapse via `display:none`. Widths live on the
              <th> so hidden cells don't leave gaps. */}
          <table className="w-full table-auto">
            <thead>
              <tr className="border-b border-border bg-surface-alt text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
                {columns.map((col, i) => (
                  <th
                    key={i}
                    scope="col"
                    style={col.width ? { width: col.width } : undefined}
                    className={`px-3 py-3 text-left sm:px-5 ${hideClass(col.hideBelow)}`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {items.map((row) => (
                <tr key={rowKey(row)} className="align-middle hover:bg-surface-alt">
                  {columns.map((col, i) => (
                    <td
                      key={i}
                      className={`px-3 py-3 sm:px-5 ${col.wrap ? "" : "truncate"} ${hideClass(col.hideBelow)} ${col.className ?? ""}`}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(hasPrev || hasNext) && (
        <nav
          aria-label="Paginação"
          className="mt-5 flex items-center justify-between gap-4"
        >
          <p className="text-xs text-text-tertiary">
            {totalPages !== null
              ? `Página ${pageIndex + 1} de ${totalPages}`
              : `Página ${pageIndex + 1}`}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={goPrev}
              disabled={!hasPrev || loading}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={13} strokeWidth={2} aria-hidden />
              Anterior
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!hasNext || loading}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-40"
            >
              {loading && pageIndex + 1 >= pages.length ? (
                <Loader2 size={13} className="animate-spin" aria-hidden />
              ) : null}
              Próxima
              <ChevronRight size={13} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
